import { useState, useEffect } from "react";
import { MainPageBase } from "../page-bases/MainPageBase";

import { Box, Stack } from "@mui/material";
import DamsTable from "../../components/Table/CollapsibleTable";

import { DamsAppAsset } from "../../assets/registry";
import { TRowItem } from "../../components/Table/TableRow";

import { UserApi } from "../../services/users";
import ActionModal from "../../components/Modals/ActionModal";
import { ModalBase } from "../../components/Modals/ModalBase";
import {
  GetUser,
  IPagination,
  IqueryParams,
  PageActions,
  UserQuery,
} from "../../config/types";
import { usePageModels } from "../../hooks/usePageActions";
import { usePagination } from "../../hooks/usePagination";
import { useMangeTableItems } from "../../hooks/useManageTableItems";
import { BulkDeleteController } from "../../common-ui/BulkDeleteController";
import appRoutePath from "../../config/path";
import useAppNavigation from "../../hooks/useAppNavigation";
import { BadgeLabel } from "../../components/Label";
import { resendVerificationMail } from "../../services/auth/resendVerificationMail";
import { actionDialogStore, alertDialogStore } from "../../store/dialogs";
import { UsersFilter } from "../../common-ui/page-filters/UsersFilter";
import { formatDate } from "../../utils/common";

function UsersMainPage() {
  const [dataset, setDataset] = useState<TRowItem[]>([]);
  const [querySet, setQuerySet] = useState<IqueryParams<UserQuery>>({
    is_deleted: false,
  });

  const [pageAction, updatePageAction] = useState<PageActions>();
  const { models, closeModel, openModel } = usePageModels();
  const [isProcessing, setProcess] = useState(false);
  const {
    selectedRowItems,
    toggleRow,
    toggleSelectAllRow,
    resetSelectedRowItems,
  } = useMangeTableItems(dataset);
  const navigate = useAppNavigation();
  const {
    pagination,
    paginateToNextPage,
    paginateToPreviousPage,
    paginateToPageNumber,
    updatePaginationInitState,
  } = usePagination<GetUser[]>(UserApi.listUsers);

  const fetchData = async () => {
    const result = await UserApi.listUsers({ is_deleted: false });
    if (result?.data) {
      populateDataSet(result.data, result.pagination);
    }
  };
  const populateDataSet = (data: GetUser[], pagination?: IPagination) => {
    const formatData = data.map((record) => ({
      ...record,
      created_at: formatDate(record.created_at),
    }));
    setDataset(formatData);
    updatePaginationInitState(pagination);
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function addNewItem() {
    navigate({ path: appRoutePath.ADD_USERS_PAGE });
  }

  function cancelAddItem() {
    navigate({ path: appRoutePath.USERS_PAGE });
  }

  function onUserItemEdit(item: TRowItem) {
    navigate({ path: appRoutePath.EDIT_USERS_PAGE, params: { id: item.id } });
  }

  async function onUserItemDelete(item: TRowItem) {
    openModel("delete");
    updatePageAction({
      id: item.id,
      name: item.email,
    });
  }
  async function handleDeleteRequest() {
    setProcess(true);
    const res = await UserApi.deleteUser(pageAction?.id ?? "");
    if (res.success) {
      fetchData();
    }
    setProcess(false);
    updatePageAction({
      title: res.success ? "User Deleted " : "Delete User Failed",
      message: res.message,
      success: res.success,
    });
    openModel("alert");
  }

  const renderHeaderDropDownMenu = ({
    closeDialog,
  }: {
    closeDialog: () => void;
  }) => {
    const batchDeleteCallback = async () => {
      resetSelectedRowItems();
      await fetchData();
      closeDialog();
    };
    return (
      <Stack direction={"column"} alignItems={"flex-start"} spacing={1}>
        <BulkDeleteController
          selectedRowItems={selectedRowItems}
          reset={batchDeleteCallback}
          ftn={UserApi.deleteUser}
          idKey={"id"}
        />
      </Stack>
    );
  };

  async function handleResendMail(email: string) {
    const res = await resendVerificationMail(email);
    if (res.success) {
      await fetchData();
      alertDialogStore.open({
        title: "Resend Verification Mail",
        subtitle: res.message,
        isSuccess: res.success,
      });
    }
  }

  async function handleRestoreUser(id: string) {
    const res = await UserApi.restoreUser(id);
    if (res.success) {
      await fetchData();
      alertDialogStore.open({
        title: "Restore Account",
        subtitle: res.message,
        isSuccess: res.success,
      });
    }
  }

  async function handleChangeStatus(id: string, activate: boolean) {
    const request = activate
      ? UserApi.restoreUser(id)
      : UserApi.deActiviateUser(id);
    const res = await request;

    if (res.success) {
      await fetchData();
    }
    alertDialogStore.open({
      title: "Update User Account Status",
      subtitle: res.message,
      isSuccess: res.success,
    });
  }

  const triggerResendMail = (email: string) => {
    actionDialogStore.open({
      title: "Resend Verification Email",
      subtitle: `Are you sure you want to resend the verification email to: ${email}?`,
      onRightBtnPress: async () => handleResendMail(email),
    });
  };

  const triggerRestoreAccount = (id: string) => {
    actionDialogStore.open({
      title: "Restore Deleted Account ",
      subtitle: `Are you sure you want to record is account?`,
      onRightBtnPress: async () => handleRestoreUser(id),
    });
  };

  const triggerChangeStatus = (id: string, activate: boolean) => {
    const { title, subtitle } = getStatusChangeDialogText(activate);
    actionDialogStore.open({
      title,
      subtitle,
      onRightBtnPress: async () => {
        handleChangeStatus(id, activate);
      },
    });
  };

  const renderTableLinkActions = ({ item }: { item: TRowItem }) => {
    const record = item as GetUser;
    const showActionButton = record.is_verified === false;
    const isDeleted = querySet.is_deleted === true;

    return (
      <Stack direction={"row"} spacing={1} pl={2}>
        {showActionButton && !isDeleted && (
          <BadgeLabel
            onClick={() => triggerResendMail(record.email)}
            title={"Resend Mail"}
            colorType="green"
            isLarge
            tip="Resend account verification mail"
          />
        )}
        {isDeleted && (
          <BadgeLabel
            onClick={() => triggerRestoreAccount(record.id)}
            title={"Restore"}
            colorType="green"
            isLarge
            tip="Restore this user account"
          />
        )}
        {!isDeleted && (
          <BadgeLabel
            onClick={() => {
              record.is_active
                ? triggerChangeStatus(record.id, false)
                : triggerChangeStatus(record.id, true);
            }}
            title={
              record.is_active === true ? "Deactivate User" : "Activate User"
            }
            colorType={record.is_active ? "red" : "green"}
            isLarge
            tip="Resend account verification mail"
          />
        )}
      </Stack>
    );
  };

  return (
    <MainPageBase
      pageState={"main"}
      title={"User Management"}
      // subtitle={"Manage all users belonging to DAMS"}
      addItemBtnOnClick={() => addNewItem()}
      addItemBtnText={"Add Users"}
      cancelBtnClick={() => cancelAddItem()}
      // searchBtnOnClick={() => searchItem()}
    >
      <Box
        height={`100%`}
        display={"flex"}
        justifyContent={"center"}
        // border={'2px solid blue'}
      >
        <Stack width={"100%"} spacing={1}>
          <UsersFilter<GetUser[]>
            ftn={UserApi.listUsers}
            updateDataSet={populateDataSet}
            updateQuerySet={setQuerySet}
          />

          <DamsTable
            tableTitle="Users"
            tableSubtitle=""
            numTableRecords="25"
            tableHeaders={columns}
            tableKeys={tkeys}
            hiddenKeys={hkeys}
            hiddenLabels={hcolumns}
            data={dataset}
            onTableItemEdit={onUserItemEdit}
            onTableItemDelete={onUserItemDelete}
            selectedRowItems={selectedRowItems}
            toggleSelectAllRow={toggleSelectAllRow}
            onTableItemSelect={toggleRow}
            maxRowCells={8}
            hasPagination
            paginationState={pagination}
            onNextPage={paginateToNextPage(querySet, populateDataSet)}
            onPreviousPage={paginateToPreviousPage(querySet, populateDataSet)}
            setActivePage={paginateToPageNumber(populateDataSet, querySet)}
            useTableCheckbox
            headerDropDownMenu={renderHeaderDropDownMenu}
            tableTitleBadge={
              selectedRowItems.length === 0
                ? undefined
                : selectedRowItems.length + " selected"
            }
            extraRowComponent={renderTableLinkActions}
          />
        </Stack>

        <ActionModal
          show={models.showAlertModel}
          title={pageAction?.title ?? ""}
          leftIcon={
            pageAction?.success === false
              ? DamsAppAsset.icons.warning
              : undefined
          }
          subtitle={pageAction?.message ?? ""}
          onLeftBtnPress={closeModel}
          leftBtnTxt={"close"}
          onRightBtnPress={closeModel}
          rightBtnTxt={"Continue"}
          onClosePress={closeModel}
        />

        <ModalBase
          open={models.showDeleteModel}
          title={"Delete User"}
          subtitle={
            <Box>
              Once you delete{" "}
              <span style={{ fontWeight: "bold" }}>{pageAction?.name}</span>,
              all related information will be lost forever. Are you sure you
              want to proceed?
            </Box>
          }
          type={"action"}
          showHeader={true}
          leftIcon={DamsAppAsset.icons.delete_icon}
          leftBtnTxt="Cancel"
          onLeftBtnPress={closeModel}
          rightBtnTxt="Delete"
          onRightBtnPress={handleDeleteRequest}
          onRightBtnLoading={isProcessing}
          onClose={closeModel}
        />
      </Box>
    </MainPageBase>
  );
}

const tkeys = [
  "first_name",
  "last_name",
  "user_domain",
  "role",
  "is_active",
  "is_verified",
  "created_at",
];

const columns = [
  "First Name",
  "Last Name",
  "Domain",
  "Role",
  "Active Status",
  "Account verified",
  "Date Added",
];

const hkeys = ["email", "telephone"];

const hcolumns = ["Email", "Telephone"];

export { UsersMainPage };
export function getStatusChangeDialogText(activate: boolean) {
  return {
    title: activate ? "Activate User Account" : "Deactivate User Account",
    subtitle: activate
      ? "This will reactivate the user's account and restore access."
      : "This will deactivate the user's account and revoke access.",
  };
}
